// @tennis-matches/services/trainerstunden/trainer-expense-categories
// Standard-Kategorien für TrainingsDesk-Ausgabenerfassung (i18n-Keys unter trainerstunden.expenses.category.*)

/** @typedef {'court_fees'|'equipment'|'training_education'|'travel'|'insurance'|'club_membership'|'marketing'|'software'|'office'|'other'} TrainerExpenseCategoryId */

/** @typedef {'standard'|'proportional_annual'} TrainerExpenseFormKind */

/** @type {readonly TrainerExpenseCategoryId[]} */
export const TRAINER_EXPENSE_CATEGORY_IDS = Object.freeze([
  "court_fees",
  "equipment",
  "training_education",
  "travel",
  "insurance",
  "club_membership",
  "marketing",
  "software",
  "office",
  "other",
]);

export const TRAINER_EXPENSE_FORM_KIND = Object.freeze({
  STANDARD: "standard",
  PROPORTIONAL_ANNUAL: "proportional_annual",
});

/** @type {Readonly<Partial<Record<TrainerExpenseCategoryId, TrainerExpenseFormKind>>>} */
const CATEGORY_FORM_KIND = Object.freeze({
  club_membership: TRAINER_EXPENSE_FORM_KIND.PROPORTIONAL_ANNUAL,
});

/**
 * @param {unknown} value
 * @returns {value is TrainerExpenseCategoryId}
 */
export function isTrainerExpenseCategoryId(value) {
  return TRAINER_EXPENSE_CATEGORY_IDS.includes(String(value || "").trim());
}

/**
 * @param {unknown} value
 * @returns {TrainerExpenseCategoryId}
 */
export function normalizeTrainerExpenseCategoryId(value) {
  const id = String(value || "").trim();
  return isTrainerExpenseCategoryId(id) ? id : "other";
}

/**
 * @param {unknown} categoryId
 * @returns {TrainerExpenseFormKind}
 */
export function resolveTrainerExpenseFormKind(categoryId) {
  const id = normalizeTrainerExpenseCategoryId(categoryId);
  return CATEGORY_FORM_KIND[id] || TRAINER_EXPENSE_FORM_KIND.STANDARD;
}

/**
 * Heuristische Kategorie aus Beleg-Kontext (Händler, Positionen, Beschreibung).
 *
 * @param {{ vendor?: unknown, description?: unknown, lineItems?: Array<{ description?: unknown, product_code?: unknown }>, items?: Array<{ description?: unknown, product_code?: unknown }> }} [context]
 * @returns {TrainerExpenseCategoryId|null}
 */
export function inferTrainerExpenseSuggestedCategoryId(context = {}) {
  const lineSource =
    Array.isArray(context.lineItems) && context.lineItems.length
      ? context.lineItems
      : Array.isArray(context.items) && context.items.length
        ? context.items
        : [];

  const chunks = [
    context.vendor,
    context.description,
    ...lineSource.flatMap((item) => [item?.description, item?.product_code]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (!chunks.trim()) return null;

  if (
    /tennis-point|pro'?s\s*pro|decathlon|sport\s*2000|intersport|sportscheck|sport\s*check|k[-\s]?swiss|quiet\s*please|bekleidung|schuh|socken|sportartikel|tennisball|ballwagen|ersatztasche|tennisschläger|racket|rackets|equipment/i.test(
      chunks,
    )
  ) {
    return "equipment";
  }
  if (
    /platzgebühr|court fee|tennisplatz|hallenbelegung|platzmiete/i.test(chunks)
  ) {
    return "court_fees";
  }
  if (
    /fortbildung|seminar|trainer\s*lizenz|coaching\s*zertifikat/i.test(chunks)
  ) {
    return "training_education";
  }
  if (
    /bahn|flug|hotel|reise|deutsche\s*bahn|mietwagen|taxi|parkgebühr/i.test(
      chunks,
    )
  ) {
    return "travel";
  }
  if (/versicherung|haftpflicht|berufsunfähigkeit/i.test(chunks)) {
    return "insurance";
  }
  if (/vereinsbeitrag|mitgliedschaft|club\s*membership/i.test(chunks)) {
    return "club_membership";
  }
  if (/werbung|marketing|flyer|anzeige|social\s*media/i.test(chunks)) {
    return "marketing";
  }
  if (
    /software|saas|lizenz|subscription|adobe|microsoft\s*365|app\s*store/i.test(
      chunks,
    )
  ) {
    return "software";
  }
  if (/büro|office|drucker|papier|porto|briefmarke/i.test(chunks)) {
    return "office";
  }

  return null;
}
